import { EventType, KnownMembership, MatrixClient, Room, RoomMember } from 'matrix-js-sdk/src/matrix';

import i18n from 'i18n/config';
import { RoleAssignment, WorkspaceRole } from 'modules/CreateWorkspaceModal/types';
import { isEmailFormat } from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import { isSystemUser } from 'utils/matrixUtils';

import { Member } from './MembersSection/utils';

type DetermineMemberRoleParams = {
  userId: string;
  currentUserId?: string | null;
  matrixClient?: MatrixClient;
  workspaceId?: string | null;
};

export const getDefaultMemberRoleName = ({ userId, matrixClient, workspaceId }: DetermineMemberRoleParams): string => {
  if (!userId || !matrixClient || !workspaceId) {
    return i18n.t('member');
  }

  const workspaceRoom = matrixClient.getRoom(workspaceId);
  const isCreator = workspaceRoom?.getCreator() === userId;
  if (isCreator) return i18n.t('owner');

  return i18n.t('member');
};

type MapRoomMembersToMembersParams = {
  roomMembers: RoomMember[];
  matrixClient: MatrixClient;
  workspaceId: string | null;
  roleAssignments?: RoleAssignment[];
  workspaceRoles?: WorkspaceRole[];
};

export const mapRoomMembersToMembers = ({
  matrixClient,
  roomMembers,
  workspaceId,
  roleAssignments = [],
  workspaceRoles = [],
}: MapRoomMembersToMembersParams): Member[] =>
  roomMembers
    .filter((member) => member.membership === KnownMembership.Join && !isSystemUser(member.userId))
    .map(({ userId, user, name, powerLevel }) => {
      const roleAssignment = roleAssignments.find((assignment) => assignment.matrixId === userId);
      const role = workspaceRoles.find(({ id }) => id === roleAssignment?.roleId);
      const roleId = role?.id;
      const resolvedRoleName = role?.name || getDefaultMemberRoleName({ userId, matrixClient, workspaceId });

      return {
        id: userId,
        name: name || user?.displayName || userId,
        roleId,
        roleName: resolvedRoleName,
        defaultRoleType: role?.defaultRoleType,
        powerLevel,
      };
    });

export const assignmentMatchesMember = (
  assignment: RoleAssignment,
  member: Pick<Member, 'id' | 'isPendingInvite'>
): boolean => {
  if (member.isPendingInvite && isEmailFormat(member.id)) {
    return assignment.pendingEmail === member.id || assignment.matrixId === member.id;
  }
  return assignment.matrixId === member.id;
};

export const assignmentMatchesInvitee = (assignment: RoleAssignment, invitee: string): boolean =>
  assignmentMatchesMember(assignment, { id: invitee, isPendingInvite: isEmailFormat(invitee) });

const resolveInviteeRole = (
  inviteeId: string,
  roleAssignments: RoleAssignment[],
  workspaceRoles: WorkspaceRole[]
): Pick<Member, 'roleId' | 'roleName' | 'defaultRoleType'> => {
  const assignment = roleAssignments.find((candidate) => assignmentMatchesInvitee(candidate, inviteeId));
  const role = workspaceRoles.find(({ id }) => id === assignment?.roleId);

  return {
    roleId: role?.id,
    roleName: role?.name || i18n.t('member'),
    defaultRoleType: role?.defaultRoleType,
  };
};

const normalizeEmailKey = (email: string): string => email.trim().toLowerCase();

const buildPendingEmailMember = (
  email: string,
  roleAssignments: RoleAssignment[],
  workspaceRoles: WorkspaceRole[]
): Member => ({
  id: email,
  email,
  name: '',
  ...resolveInviteeRole(email, roleAssignments, workspaceRoles),
  powerLevel: 0,
  isPendingInvite: true,
});

type ThirdPartyInviteContent = {
  display_name?: string;
  medium?: string;
  address?: string;
};

type ThirdPartyInviteEvent = {
  getContent: () => ThirdPartyInviteContent;
  getId?: () => string | undefined;
  isRedacted?: () => boolean;
};

// Prefer display_name; fall back to address when display_name is not email-shaped (same as Synapse email invites).
export const getThirdPartyInviteEmailFromContent = (content: ThirdPartyInviteContent): string | null => {
  if (content.display_name && isEmailFormat(content.display_name)) return content.display_name;
  if (content.medium === 'email' && content.address && isEmailFormat(content.address)) return content.address;
  return null;
};

export const findThirdPartyInviteEventIdForEmail = (
  thirdPartyEvents: ThirdPartyInviteEvent[],
  email: string
): string | undefined => {
  const targetKey = normalizeEmailKey(email);

  const matchingEvent = thirdPartyEvents.find((event) => {
    if (event.isRedacted?.()) return false;

    const inviteEmail = getThirdPartyInviteEmailFromContent(event.getContent());
    return inviteEmail !== null && normalizeEmailKey(inviteEmail) === targetKey;
  });

  return matchingEvent?.getId?.();
};

type MapThirdPartyEmailInvitesParams = {
  workspaceRoom: Room;
  roleAssignments: RoleAssignment[];
  workspaceRoles: WorkspaceRole[];
  redeemedEmailKeys?: ReadonlySet<string>;
};

type ThirdPartySignedContent = {
  token?: string;
};

export const collectRedeemedThirdPartyInviteEmails = (
  workspaceRoom: Room,
  roomMembers: RoomMember[],
  roleAssignments: RoleAssignment[] = []
): Set<string> => {
  const redeemedTokens = new Set<string>();

  for (const event of workspaceRoom.getLiveTimeline().getEvents()) {
    if (event.getType() !== EventType.RoomMember) continue;
    if (event.getContent()?.membership !== KnownMembership.Join) continue;

    const token = (event.getContent()?.third_party_signed as ThirdPartySignedContent | undefined)?.token;
    if (typeof token === 'string' && token.length > 0) {
      redeemedTokens.add(token);
    }
  }

  for (const member of roomMembers) {
    if (member.membership !== KnownMembership.Join) continue;

    const memberEvent = workspaceRoom.currentState.getStateEvents(EventType.RoomMember, member.userId);
    const token = (memberEvent?.getContent()?.third_party_signed as ThirdPartySignedContent | undefined)?.token;
    if (typeof token === 'string' && token.length > 0) {
      redeemedTokens.add(token);
    }
  }

  const redeemedEmails = new Set<string>();

  const thirdPartyEvents = workspaceRoom.currentState.getStateEvents(EventType.RoomThirdPartyInvite);
  for (const event of thirdPartyEvents) {
    const token = event.getStateKey();
    if (!token || !redeemedTokens.has(token)) continue;

    const email = getThirdPartyInviteEmailFromContent(event.getContent() as ThirdPartyInviteContent);
    if (email) redeemedEmails.add(normalizeEmailKey(email));
  }

  // Fallback when join is confirmed via roles reconciliation but the third-party invite state is stale.
  for (const assignment of roleAssignments) {
    if (assignment.pendingEmail && assignment.matrixId) {
      redeemedEmails.add(normalizeEmailKey(assignment.pendingEmail));
    }
  }

  return redeemedEmails;
};

export const mapThirdPartyEmailInvites = ({
  workspaceRoom,
  roleAssignments,
  workspaceRoles,
  redeemedEmailKeys = new Set(),
}: MapThirdPartyEmailInvitesParams): Member[] => {
  const thirdPartyEvents = workspaceRoom.currentState.getStateEvents(EventType.RoomThirdPartyInvite);
  const seenEmails = new Set<string>();

  return thirdPartyEvents.flatMap((event) => {
    if (event.isRedacted?.()) return [];

    const email = getThirdPartyInviteEmailFromContent(event.getContent() as ThirdPartyInviteContent);
    if (!email) return [];

    const emailKey = normalizeEmailKey(email);
    if (seenEmails.has(emailKey) || redeemedEmailKeys.has(emailKey)) return [];

    seenEmails.add(emailKey);
    return [buildPendingEmailMember(email, roleAssignments, workspaceRoles)];
  });
};

type MapInvitedMatrixMembersParams = {
  roomMembers: RoomMember[];
  matrixClient: MatrixClient;
  workspaceId: string | null;
  roleAssignments?: RoleAssignment[];
  workspaceRoles?: WorkspaceRole[];
  excludeUserIds?: ReadonlySet<string>;
};

export const mapInvitedMatrixMembers = ({
  matrixClient: _matrixClient,
  roomMembers,
  workspaceId: _workspaceId,
  roleAssignments = [],
  workspaceRoles = [],
  excludeUserIds = new Set(),
}: MapInvitedMatrixMembersParams): Member[] =>
  roomMembers
    .filter(
      (member) =>
        member.membership === KnownMembership.Invite &&
        !isSystemUser(member.userId) &&
        !excludeUserIds.has(member.userId)
    )
    .map(({ userId, user, name, powerLevel }) => ({
      id: userId,
      name: name || user?.displayName || userId,
      ...resolveInviteeRole(userId, roleAssignments, workspaceRoles),
      powerLevel,
      isPendingInvite: true,
    }));

type BuildWorkspaceMembersListParams = {
  roomMembers: RoomMember[];
  matrixClient: MatrixClient;
  workspaceId: string | null;
  workspaceRoom: Room;
  roleAssignments: RoleAssignment[];
  workspaceRoles: WorkspaceRole[];
};

export const buildWorkspaceMembersList = ({
  roomMembers,
  matrixClient,
  workspaceId,
  workspaceRoom,
  roleAssignments,
  workspaceRoles,
}: BuildWorkspaceMembersListParams): Member[] => {
  const joinedMembers = mapRoomMembersToMembers({
    roomMembers,
    matrixClient,
    workspaceId,
    workspaceRoles,
    roleAssignments,
  });
  const joinedUserIds = new Set(joinedMembers.map((member) => member.id));
  const redeemedEmailKeys = collectRedeemedThirdPartyInviteEmails(workspaceRoom, roomMembers, roleAssignments);

  const pendingFromThirdParty = mapThirdPartyEmailInvites({
    workspaceRoom,
    roleAssignments,
    workspaceRoles,
    redeemedEmailKeys,
  });

  const pendingFromMatrixInvite = mapInvitedMatrixMembers({
    roomMembers,
    matrixClient,
    workspaceId,
    roleAssignments,
    workspaceRoles,
    excludeUserIds: joinedUserIds,
  });

  return [...joinedMembers, ...pendingFromThirdParty, ...pendingFromMatrixInvite];
};
